import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Public: "Is my site a fit for the move-your-site class?" Checks the site, then
// sends a fit to the class page and a "not yet" to the waitlist. ?class=<vanity>
// names the class page; without it the button goes to /my-site.
interface Fit { ok: boolean; error?: string; title?: string; platform?: string; store?: boolean; members?: boolean; booking?: boolean; pages?: number; verdict?: 'great' | 'maybe' | 'later'; reasons?: string[] }

const card: React.CSSProperties = { background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', borderRadius: 'var(--hj-r-md)', padding: '18px 18px 16px', marginTop: 16 };

const MoveYourSitePage: React.FC = () => {
  const [params] = useSearchParams();
  const classCode = params.get('class');
  const [url, setUrl] = useState('');
  const [email, setEmail] = useState('');
  const [fit, setFit] = useState<Fit | null>(null);
  const [busy, setBusy] = useState(false);
  const [listed, setListed] = useState(false);
  const check = async () => {
    setBusy(true); setFit(null); setListed(false);
    try {
      const r = await fetch('/api/sites/fit-check', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url, email }) });
      setFit(await r.json());
    } catch { setFit({ ok: false, error: 'Something went wrong. Try again.' }); }
    setBusy(false);
  };
  const classHref = classCode ? `/w/${classCode}` : '/my-site';
  return (
    <main className="mx-auto" style={{ maxWidth: 680, padding: '0 16px 96px', background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}>
      <header style={{ margin: '24px 0 8px', padding: '22px 22px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
        <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-beige-ink-2)' }}>Hey Joe Sites</div>
        <h1 className="font-hj-display m-0" style={{ fontSize: 'clamp(26px, 4vw, 38px)', lineHeight: 1.05, letterSpacing: '-0.02em', fontWeight: 700, color: 'var(--hj-beige-ink)' }}>
          Is your site ready to move?
        </h1>
        <p style={{ margin: '12px 0 0', fontSize: 15 }}>Enter your website. We'll look at it and tell you if it can move in an afternoon.</p>
      </header>

      <div style={card}>
        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Your website</label>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="janedoe.com" inputMode="url" autoCapitalize="none" aria-label="Your website" />
        <label style={{ fontSize: 13, fontWeight: 600, display: 'block', margin: '12px 0 6px' }}>Your email</label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@email.com" aria-label="Your email" />
        <Button style={{ marginTop: 14 }} disabled={!url || busy} onClick={check}>{busy ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Looking at your site</> : 'Check my site'}</Button>
      </div>

      {fit && !fit.ok && <div style={{ ...card, color: 'var(--hj-pink-ink)' }}>{fit.error}</div>}

      {fit && fit.ok && (
        <div style={{ ...card, background: fit.verdict === 'later' ? 'var(--hj-surface-1)' : 'var(--hj-yellow-tint)', borderColor: fit.verdict === 'later' ? 'var(--hj-hairline)' : 'var(--hj-yellow)' }}>
          <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: 17 }}>
            {fit.verdict === 'great' ? 'Good news: your site is a great fit.' : fit.verdict === 'maybe' ? 'Your site can move, with some work.' : 'Not this class, but soon.'}
          </p>
          <p style={{ margin: '0 0 8px', fontSize: 14, color: 'var(--hj-ink-2)' }}>
            {fit.title ? `${fit.title} · ` : ''}{fit.platform}{fit.pages ? ` · about ${fit.pages} pages` : ''}
          </p>
          {(fit.reasons || []).map((r) => <p key={r} style={{ margin: '0 0 6px', fontSize: 14 }}>{r}</p>)}
          {fit.verdict !== 'later' ? (
            <Button asChild style={{ marginTop: 8 }}><Link to={classHref}>Save my seat</Link></Button>
          ) : listed ? (
            <p style={{ margin: '8px 0 0', fontSize: 14 }}>You're on the list. We'll email you when there's a class for sites like yours.</p>
          ) : (
            <Button variant="outline" style={{ marginTop: 8 }} disabled={!email} onClick={async () => {
              await fetch('/api/sites/fit-check/waitlist', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, url, reason: (fit.reasons || [])[0] || '' }) }).catch(() => {});
              setListed(true);
            }}>{email ? 'Tell me when there\'s a class for my site' : 'Add your email above to join the list'}</Button>
          )}
        </div>
      )}
    </main>
  );
};

export default MoveYourSitePage;
